import { useEffect, useRef } from 'react';
import { Shield } from 'lucide-react';
import { Button } from '../../ui/button';
import { formatCurrency } from '../HiveFundInvestFlow.utils';

interface ReviewStepProps {
  investmentAmount: number;
  paymentMethod: 'bank' | 'crypto' | 'wire' | 'investment_account';
  agreementAccepted: boolean;
  onAgreementChange: (accepted: boolean) => void;
  acceptTerms: boolean;
  onAcceptTermsChange: (accepted: boolean) => void;
  acceptRiskDisclosure: boolean;
  onAcceptRiskDisclosureChange: (accepted: boolean) => void;
  acceptAchAuth: boolean;
  onAcceptAchAuthChange: (accepted: boolean) => void;
  acceptCryptoIrrevocable: boolean;
  onAcceptCryptoIrrevocableChange: (accepted: boolean) => void;
  onConfirm: () => void;
  canConfirm: boolean;
}

export function ReviewStep({
  investmentAmount,
  paymentMethod,
  agreementAccepted,
  onAgreementChange,
  acceptTerms,
  onAcceptTermsChange,
  acceptRiskDisclosure,
  onAcceptRiskDisclosureChange,
  acceptAchAuth,
  onAcceptAchAuthChange,
  acceptCryptoIrrevocable,
  onAcceptCryptoIrrevocableChange,
  onConfirm,
  canConfirm,
}: ReviewStepProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const estimatedQuarterlyLow = (investmentAmount * 0.08) / 4;
  const estimatedQuarterlyHigh = (investmentAmount * 0.12) / 4;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h1
          ref={headingRef}
          tabIndex={-1}
          className="text-3xl md:text-4xl mb-2 focus-visible:outline-none"
        >
          Review your investment
        </h1>
        <p className="text-lg text-muted-foreground">
          Please confirm all details are correct
        </p>
      </div>

      {/* Investment summary */}
      <div className="rounded-xl border-2 border-border bg-card p-6">
        <h3 className="text-lg font-medium mb-4">Investment Details</h3>
        <dl className="divide-y divide-border">
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Fund</dt>
            <dd className="font-medium">The Hive Fund</dd>
          </div>
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Investment Amount</dt>
            <dd className="font-medium">{formatCurrency(investmentAmount)}</dd>
          </div>
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Payment Method</dt>
            <dd className="font-medium">
              {paymentMethod === 'bank' ? 'Bank Transfer (ACH)'
                : paymentMethod === 'wire' ? 'Wire Transfer'
                : paymentMethod === 'investment_account' ? 'Investment Account'
                : 'Cryptocurrency'}
            </dd>
          </div>
          <div className="flex items-center justify-between py-3">
            <dt className="text-muted-foreground">Est. Quarterly Distribution</dt>
            <dd className="font-medium">
              {formatCurrency(estimatedQuarterlyLow)} – {formatCurrency(estimatedQuarterlyHigh)}
            </dd>
          </div>
        </dl>
      </div>

      {/* Trust indicators */}
      <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <Shield className="w-4 h-4 text-muted-foreground" />
          SEC-regulated
        </span>
        <span className="flex items-center gap-1.5">
          <Shield className="w-4 h-4 text-muted-foreground" />
          256-bit encryption
        </span>
      </div>

      {/* Required Agreements */}
      <div className="rounded-xl border-2 border-border bg-card p-6 space-y-4">
        <p className="text-sm font-medium flex items-center gap-1.5">
          <Shield className="w-4 h-4 text-muted-foreground" /> Required Agreements
        </p>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agreementAccepted}
            onChange={(e) => onAgreementChange(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm text-muted-foreground leading-relaxed">
            I understand and accept that this is a Reg D investment in The Hive Fund. I have read
            and agree to the{' '}
            <span className="text-foreground underline">Investment Agreement</span>{' '}
            and{' '}
            <span className="text-foreground underline">Private Placement Memorandum</span>.
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => onAcceptTermsChange(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm text-muted-foreground leading-relaxed">
            I have read and agree to the{' '}
            <span className="text-foreground underline">Terms of Service</span> and{' '}
            <span className="text-foreground underline">Privacy Policy</span>.
          </span>
        </label>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptRiskDisclosure}
            onChange={(e) => onAcceptRiskDisclosureChange(e.target.checked)}
            className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
          />
          <span className="text-sm text-muted-foreground leading-relaxed">
            I acknowledge the{' '}
            <span className="text-foreground underline">Risk Disclosures</span> associated with
            this investment, including the risks of real estate and private securities.
          </span>
        </label>

        {(paymentMethod === 'bank' || paymentMethod === 'wire') && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptAchAuth}
              onChange={(e) => onAcceptAchAuthChange(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              I authorize Honeyshares to initiate a{' '}
              <span className="text-foreground">{paymentMethod === 'wire' ? 'bank transfer' : 'one-time ACH debit'}</span>{' '}
              of <span className="text-foreground">{formatCurrency(investmentAmount)}</span> from my
              linked bank account for this transaction, subject to NACHA Operating Rules and my
              financial institution's policies.
            </span>
          </label>
        )}

        {paymentMethod === 'crypto' && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={acceptCryptoIrrevocable}
              onChange={(e) => onAcceptCryptoIrrevocableChange(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-muted-foreground leading-relaxed">
              I understand that{' '}
              <span className="text-foreground">cryptocurrency transactions are irreversible</span>{' '}
              and cannot be refunded or reversed once submitted.
            </span>
          </label>
        )}
      </div>

      <Button
        className="w-full h-14 text-lg disabled:opacity-40"
        onClick={onConfirm}
        disabled={!canConfirm}
      >
        Confirm Investment
      </Button>
    </div>
  );
}
